import { useState, useEffect, useMemo, useCallback } from 'react';
import DatePicker from 'react-datepicker';
import { useWindowSize } from 'react-use';
import {enAU} from "date-fns/locale/en-AU";
import { max, min, format, parse, isSameDay, isWeekend } from 'date-fns';

type DatepickerProps = {
  name: string;
  required: boolean;
  past?: boolean;// past dates only if true
  future?: boolean;// future dates only if true
  callBack: (date: Date) => void;
  selected?: string | Date;
  withPortal?: boolean;
  minDate?: string | Date;
  maxDate?: string | Date;
  showMonthDropdown?: boolean;
  showYearDropdown?: boolean;
  dropdownMode?: "select" | "scroll";
  holidays?: boolean;
  waholidays?: string
  excludeDates?: Array<Date | string> | string;
  tracking?: string;
  fieldname?: string;
  fieldkey?: string;
  val?: string;
  disablefocustracking?: boolean
};

const Datepicker = ({
  name,
  required,
  past,
  future,
  callBack,
  selected,
  withPortal,
  minDate,
  maxDate,
  showMonthDropdown = false,
  showYearDropdown = false,
  dropdownMode = "scroll",
  holidays,
  waholidays = "04/18/2025,04/21/2025,04/25/2025,06/02/2025,09/29/2025,12/25/2025,12/26/2025,01/01/2026,01/26/2026,03/02/2026,04/03/2026,04/06/2026,04/27/2026,06/01/2026,09/28/2026,12/25/2026,12/28/2026,01/01/2027,01/26/2027,03/01/2027,03/26/2027,03/29/2027,04/26/2027,06/07/2027,09/27/2027,12/27/2027,12/28/2027",
  excludeDates,
  tracking,
  fieldname,
  fieldkey,
  val,
  disablefocustracking
}: DatepickerProps) => {
  const parseDate = (date: string | Date | undefined): Date | undefined => {
    if (!date) return undefined;
    if (date instanceof Date) return date;
    try {
      // Parse MM/DD/YYYY format
      return parse(date, 'MM/dd/yyyy', new Date());
    } catch (error) {
      console.error('Error parsing date:', error);
      return undefined;
    }
  };
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [value, setValue] = useState("");
  const { width } = useWindowSize();
  const [parsedDates, setParsedDates] = useState<{
    minDate?: Date;
    maxDate?: Date;
  }>({
    minDate: undefined,
    maxDate: undefined
  });

  useEffect(() => {
    if(selected) {
      let d = parseDate(selected)
      setValue(formatDateToDDMMYYYY(new Date(selected)))
      setSelectedDate(d);
    }
  }, [selected]);

  useEffect(() => {
    const today = new Date();
    const start = parseDate(minDate);
    const end = parseDate(maxDate);
    
    if (past) {
      // Past dates only - cap maxDate at today
      setParsedDates({
        minDate: start, // Allow any minimum date
        maxDate: end ? min([end, today]) : today
      });
    } else if (future) {
      // Future dates only - ensure minDate isn't before today
      setParsedDates({
        minDate: max([start || today, today]),
        maxDate: end
      });
    } else {
      // Both past and future allowed
      setParsedDates({
        minDate: start,
        maxDate: end
      });
    }
  }, [minDate, maxDate, past, future]);

  // Convert a date to dd/mm/yyyy format
  const formatDateToDDMMYYYY = (date?: Date): string => {
    if (!date) return "";
    return format(date, 'dd/MM/yyyy');
  };

  const handleChange = (date: Date| null) => {
    if(!date) return;
    setSelectedDate(date);
    setValue(formatDateToDDMMYYYY(date))
    if (callBack) callBack(date);
  };

const excluded = useMemo(() => {
  if(typeof excludeDates === "string") {
    return excludeDates.split(",")?.map(d => new Date(d));
  }
  return excludeDates?.map(d => new Date(d));
}, [excludeDates])

const waHolidays = useMemo(() => {
  if(holidays && waholidays) {
    return waholidays.split(',')
  }
}, [holidays, waholidays])

const isAvailableDate = useCallback((date: Date) => {
  if(!waHolidays) return true;
  // Check if it's not a weekend
  if (isWeekend(date)) return false;
  return !waHolidays.some(holiday => isSameDay(date, holiday));
}, [waHolidays]);

  return (
    <>
       <input
        type="hidden"
        name={name}
        value={value}
        data-tracking={tracking}
        data-fieldname={fieldname}
        data-fieldkey={fieldkey}
        data-val={val}
        data-val-required={required}
        data-disablefocustracking={disablefocustracking}
      />
      <DatePicker
        placeholderText="--/--/----"
        dateFormat="dd/MM/yyyy"
        className="input-calendar"
        selected={selectedDate ?? null}
        locale={enAU}
        onChange={handleChange}
        withPortal={withPortal || width < 500}
        minDate={parsedDates.minDate}
        maxDate={parsedDates.maxDate}
        showMonthDropdown={showMonthDropdown}
        showYearDropdown={showYearDropdown}
        dropdownMode={dropdownMode}
        excludeDates={excluded}
        filterDate={isAvailableDate}
      />
    </>
    
  );
};

export default Datepicker;
